import React, { useMemo } from 'react';
import Section from '../../../layout/Section';
import HorizontalTable, { HorizontalTableRow } from '../../../tables/HorizontalTable';
import { GuVYear } from '../hooks/useCalculationPageData';

interface TradeTaxSectionProps {
  data: GuVYear[];
}

/**
 * Gewerbesteuer-Berechnungsdetails als Tabelle
 * Zeigt die vom Backend berechneten Zwischenschritte der Gewerbesteuerberechnung:
 * - Ergebnis vor Steuern
 * - Hinzurechnung (Zinsen / 2)
 * - Maßgebender Gewinnertrag
 * - Verlustvortrag
 * - Freibetrag
 * - Zu versteuernder Ertrag
 * - Gewerbesteuer
 * 
 * Alle Werte werden direkt vom Backend geliefert - keine Frontend-Berechnung.
 */
const TradeTaxSection: React.FC<TradeTaxSectionProps> = ({ data }) => {
  // Sortiere Daten nach Jahr
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => a.jahr - b.jahr);
  }, [data]);

  // Spaltenüberschriften (Jahre)
  const columnHeaders = useMemo(() =>
    sortedData.map(d => d.jahr),
    [sortedData]
  );

  // Zeilen für HorizontalTable - direkt aus den Backend-Daten
  const rows: HorizontalTableRow[] = useMemo(() => [
    {
      key: 'ergebnisVorSteuern',
      label: 'Ergebnis vor Steuern',
      values: sortedData.map(d => d.ergebnisVorSteuern),
    },
    {
      key: 'hinzurechnung',
      label: '+ Hinzurechnung (Zinsen ÷ 2)',
      values: sortedData.map(d => d.hinzurechnung),
    },
    {
      key: 'massgebenderGewinnertrag',
      label: '= Maßgebender Gewinnertrag',
      values: sortedData.map(d => d.massgebenderGewinnertrag),
      highlightRow: true,
    },
    {
      key: 'verlustvortrag',
      label: '- Verlustvortrag',
      values: sortedData.map(d => d.verlustvortrag),
    },
    {
      key: 'freibetrag',
      label: '- Freibetrag',
      values: sortedData.map(d => d.gewerbeFreibetragVerwendet),
    },
    {
      key: 'zuVersteuernderErtrag',
      label: '= Zu versteuernder Ertrag',
      values: sortedData.map(d => d.zuVersteuernderErtrag),
      highlightRow: true,
    },
    {
      key: 'gewerbesteuer',
      label: 'Gewerbesteuer',
      values: sortedData.map(d => d.gewerbesteuer),
      highlightRow: true,
    },
  ], [sortedData]);

  return (
    <Section title="Gewerbesteuer-Berechnung" noPadding collapsible defaultExpanded={false}>
      <HorizontalTable
        headerLabel="Jahr"
        columnHeaders={columnHeaders}
        rows={rows}
        labelColumnWidth={200}
        dataColumnWidth={120}
      />
    </Section>
  );
};

export default React.memo(TradeTaxSection);
